import type { FC, ReactNode } from 'react'
import React from 'react'

import ChevronRightIcon from 'mdi-react/ChevronRightIcon'
import ClockOutlineIcon from 'mdi-react/ClockOutlineIcon'
import PhoneRingOutlineIcon from 'mdi-react/PhoneRingOutlineIcon'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import type { Notification } from '@/api/notifications'
import { useAppContext } from '@/App.context'
import { LocalDate } from '@/components/LocalDate'
import { APP_ROUTES } from '@/util/appRoutes'

import { Translation } from '../i18n/Translation'

type CardProps = {
  data: Notification
}

export const Activity: React.FC<CardProps> = ({ data }) => {
  const navigate = useNavigate()

  if (data.notificationType === 'buyout_offer') {
    return (
      <ActivityCardItem
        title={
          <Translation
            id="You have a buyout offer for {assetName}"
            values={{ assetName: data.assetDescription }}
          />
        }
        dateParagraph={
          <Translation
            id="Offer expires: {date}"
            date={<LocalDate date={data.offerExpirationDate} />}
          />
        }
        onClick={() => navigate(APP_ROUTES.myAssets, { state: { scrollToAssetId: data.assetId } })}
        onKeyDown={() =>
          navigate(APP_ROUTES.myAssets, { state: { scrollToAssetId: data.assetId } })
        }
        expiryDate={
          typeof data.offerExpirationDate === 'string'
            ? new Date(data.offerExpirationDate)
            : data.offerExpirationDate
        }
      />
    )
  }

  if (data.notificationType === 'end_of_lease_decision_deadline') {
    return (
      <ActivityCardItem
        title={<Translation id="Lease ending - time to order new equipment" />}
        dateParagraph={<Translation id="Order by {date}" date={<LocalDate date={data.date} />} />}
        onClick={() =>
          navigate(
            `${APP_ROUTES.ecomPolicyList}/` +
              APP_ROUTES.ecomPolicy.replace(':ecomPolicyId', data.policyId.toString())
          )
        }
        onKeyDown={() =>
          navigate(
            `${APP_ROUTES.ecomPolicyList}/` +
              APP_ROUTES.ecomPolicy.replace(':ecomPolicyId', data.policyId.toString())
          )
        }
        expiryDate={typeof data.date === 'string' ? new Date(data.date) : data.date}
      />
    )
  }

  const onClick = () => {
    if (data.type === 'return-device') {
      return navigate(`return-device/${data.id}`)
    }

    // TODO: Temporary fix until Workflow and App is in sync BUG#4933
    if (['AssetEndOfLeaseWorkflow', 'AssetEndOfLeaseEmployeeWorkflow'].includes(data.type)) {
      return navigate(`end-of-lease/${data.id}`)
    }
  }

  const date = data.activities[0].dueDate

  return (
    <ActivityCardItem
      title={
        <Translation
          id="Your {assetName} is set for callback. Please make a choice"
          values={{ assetName: data.asset.assetDescription }}
        />
      }
      dateParagraph={<Translation id="Due date: {date}" date={<LocalDate date={date} />} />}
      onClick={onClick}
      onKeyDown={onClick}
      expiryDate={typeof date === 'string' ? new Date(date) : date}
    />
  )
}

const ActivityCardItem: FC<{
  title: ReactNode
  dateParagraph: ReactNode
  onClick: () => void
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void
  expiryDate: Date
}> = ({ title, dateParagraph, onClick, onKeyDown, expiryDate }) => {
  const { whiteLabel } = useAppContext()
  const isExpired = expiryDate < new Date()
  const isExpiringSoon = !isExpired && expiryDate < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={onKeyDown}
      className={twJoin(
        'card shadow-basic mobile:p-4 flex w-full justify-between gap-8 p-2 hover:cursor-pointer sm:p-6',
        'rounded-md',
        // 'border-solid border-x-transparent border-b-gray-300/50 border-t-transparent first:rounded-tl-md first:rounded-tr-md last:rounded-bl-md last:rounded-br-md last:border-b-transparent',
        whiteLabel === 'telia'
          ? 'bg-white'
          : whiteLabel === 'threeStepIT'
            ? 'bg-threeStepIT-cool/50'
            : 'bg-zinc-100'
      )}
    >
      <div className="flex flex-1 flex-row justify-center gap-4">
        <div
          className={twJoin(
            'mobile:flex hidden h-fit w-fit flex-col items-center justify-center self-center rounded-full p-2 sm:p-3',
            isExpired &&
              (whiteLabel === 'telia'
                ? 'bg-telia-red/20 text-telia-red'
                : whiteLabel === 'threeStepIT'
                  ? 'bg-threeStepIT-failure/20 text-threeStepIT-failure'
                  : 'bg-red-100 text-red-600'),
            isExpiringSoon &&
              (whiteLabel === 'threeStepIT'
                ? 'bg-threeStepIT-warning-light text-threeStepIT-warning'
                : 'bg-amber-100 text-amber-700'),
            !isExpired && !isExpiringSoon && 'bg-gray-200 text-gray-600'
          )}
        >
          <PhoneRingOutlineIcon />
        </div>
        <div className="flex flex-1 flex-col justify-center gap-1">
          <p className="m-0 text-base text-gray-600">{title}</p>
          <div
            className={twJoin(
              'flex items-center gap-1 text-lg',
              isExpired &&
                (whiteLabel === 'telia'
                  ? 'text-telia-red'
                  : whiteLabel === 'threeStepIT'
                    ? 'text-threeStepIT-failure'
                    : 'text-red-600'),
              isExpiringSoon &&
                (whiteLabel === 'threeStepIT' ? 'text-threeStepIT-warning' : 'text-yellow-600'),
              !isExpired && !isExpiringSoon && 'text-gray-600'
            )}
          >
            <ClockOutlineIcon />
            <p className="m-0 font-extrabold">{dateParagraph}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center text-3xl text-gray-500">
        <ChevronRightIcon />
      </div>
    </div>
  )
}
